import { useEffect } from 'react';
import { useLocation } from '@tanstack/react-router';

import { useRunOnce } from '@/hooks/use-run-once';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { isPostHogEnabled, posthog } from '@/posthog';

import { logger } from '../logger';
import { cioanalytics } from './cio';
import { identifyUser } from './identifyUser';

/**
 * Hook to automatically track page views when route changes
 * No-op if Customer.io is disabled
 */
function useTrackPageView() {
  const location = useLocation();

  useEffect(
    function trackPageView() {
      if (!cioanalytics) {
        return;
      }

      cioanalytics.page({
        path: location.pathname,
      });
    },
    [location.pathname]
  );
}

/**
 * Hook to identify the user if they have a contact email
 */
export function useIdentifyUser() {
  const email = useSessionStore((state) => state.contactEmail);

  useEffect(
    function identifyUserOnEmailChange() {
      if (email) {
        identifyUser(email);
      }
    },
    [email]
  );
}

/**
 * Utility function to end a tracking session
 * Resets Customer.io (if available) and PostHog (if enabled); no-op if both are disabled
 */
export function endTrackingSession() {
  if (cioanalytics) {
    cioanalytics.reset();
  }

  if (isPostHogEnabled) {
    posthog.reset();
  }
}

/**
 * Utility function to end a tracking session if there's a mismatch between Customer.io's
 * tracking state and the application's user state.
 *
 * Detects when Customer.io has an active tracking session (cioTrackingSessionId exists)
 * but the application doesn't have a user email in the store. This mismatch can occur
 * when user data is cleared but Customer.io still maintains a session, or when state
 * gets out of sync.
 *
 * If a mismatch is detected, resets both Customer.io and PostHog tracking sessions
 * to prevent tracking the wrong user or maintaining stale sessions.
 *
 * No-op if Customer.io is disabled.
 */
async function endTrackingSessionIfMismatchedState() {
  if (!cioanalytics) {
    return;
  }

  const email = useSessionStore.getState().contactEmail;

  try {
    const cioUser = await cioanalytics.user();
    const cioTrackingSessionId = cioUser.id();

    // ? Note that we're using the user's contact email as the unique identifier for the tracking session
    if (cioTrackingSessionId && !email) {
      endTrackingSession();
    }
  } catch (error) {
    logger.error(error, { message: 'Error checking CIO analytics user state' });
  }
}

/**
 * Hook to track analytics session with the following logic:
 * - End the tracking session if the user's state is mismatched
 * - Identify the user if they have a contact email
 * - Track the page view
 *
 * Note: Requires hook to be used within a Router context
 */
export function useAnalyticsTracking() {
  useRunOnce(() => {
    endTrackingSessionIfMismatchedState();
  });

  useIdentifyUser();
  useTrackPageView();
}
